import _extends from "@babel/runtime/helpers/esm/extends";
import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
import React, { forwardRef, useRef } from 'react';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';
import { ScopedCssBaseline, FocusStyle } from '../StyleBase';
import InputStyleBase from '../InputBase';
import { isFunction, useMergeRefs } from '../helper';
import Affix from './Affix';
import { useFormControl } from '../Form';
const Root = styled.div.withConfig({
  displayName: "Input__Root",
  componentId: "x3i8n9-0"
})(["", " display:block;align-items:center;min-width:300px;"], ScopedCssBaseline);
const Control = styled.div.withConfig({
  displayName: "Input__Control",
  componentId: "x3i8n9-1"
})(["", " ", " display:flex;padding:var(--size-spacing-02) var(--size-spacing-03);background:var(--color-white);border:1px solid var(--color-gray-60);border-radius:var(--border-radius-rounded);&:hover,&:focus-within{border-color:var(--color-gray-80);}", " ", " ", ""], ScopedCssBaseline, FocusStyle, ({
  error
}) => error && css(["border:1px solid var(--color-red-60);"]), ({
  success
}) => success && css(["border:1px solid var(--color-green-60);"]), ({
  disabled
}) => disabled && css(["background:var(--color-gray-20);border-color:var(--color-gray-20);"]));
const InputBase = styled(InputStyleBase).withConfig({
  displayName: "Input__InputBase",
  componentId: "x3i8n9-2"
})(["min-height:24px;box-shadow:none;", ""], ({
  disabled
}) => disabled && css(["background:var(--color-gray-20);color:var(--color-gray-80);"]));
const Input = /*#__PURE__*/forwardRef((_ref, ref) => {
  let {
    className,
    rootRef,
    value,
    type,
    style,
    prefix,
    suffix,
    error,
    success,
    disabled,
    onChange
  } = _ref,
      rest = _objectWithoutPropertiesLoose(_ref, ["className", "rootRef", "value", "defaultValue", "type", "style", "prefix", "suffix", "error", "success", "disabled", "children", "onChange"]);

  const inputRef = useRef();
  const {
    required: requiredFormControl,
    error: errorFormControl,
    success: successFormControl
  } = useFormControl();
  const required = requiredFormControl || rest.required;
  const isSuccess = successFormControl || success;
  const isError = errorFormControl || error;

  const handleChange = event => {
    isFunction(onChange) && onChange(event);
  };

  const handleClick = () => {
    inputRef.current.focus();
  };

  const {
    prefix: PREFIX,
    suffix: SUFFIX
  } = Affix.categories;
  return /*#__PURE__*/React.createElement(Root, {
    ref: rootRef,
    className: className,
    style: style
  }, /*#__PURE__*/React.createElement(Control, {
    error: isError,
    success: isSuccess,
    disabled: disabled,
    onClick: handleClick
  }, prefix && /*#__PURE__*/React.createElement(Affix, {
    category: PREFIX
  }, prefix), /*#__PURE__*/React.createElement(InputBase, _extends({}, rest, {
    "aria-required": required,
    ref: useMergeRefs([inputRef, ref]),
    disabled: disabled,
    value: value,
    type: type,
    "aria-disabled": disabled,
    "aria-invalid": isError,
    onChange: handleChange
  })), suffix && /*#__PURE__*/React.createElement(Affix, {
    category: SUFFIX
  }, suffix)));
});
Input.displayName = 'Input';
Input.defaultProps = {
  prefix: null,
  suffix: null,
  error: false,
  success: false
};
process.env.NODE_ENV !== "production" ? Input.propTypes = {
  /**
   * Sets if input is disabled
   */
  disabled: PropTypes.bool,

  /**
   * Sets if input is invalid
   */
  error: PropTypes.bool,

  /**
   * Sets if input is success
   */
  success: PropTypes.bool,

  /**
   * Sets the placeholder
   */
  placeholder: PropTypes.string,

  /**
   * Sets input's prefix
   */
  prefix: PropTypes.node,

  /**
   * Sets input's suffix
   */
  suffix: PropTypes.node,

  /**
   * Sets the value. Used for **Uncontrolled** component
   */
  defaultValue: PropTypes.any,

  /**
   * Sets input's value. Use it for controlled component or `defaultValue` for uncontrolled.
   */
  value: PropTypes.any,

  /**
   * Callback fired when the value is changed. `function(event: object) = {}`.
   * You can pull out the new value by accessing event.target.value (string).
   */
  onChange: PropTypes.func
} : void 0;
export default Input;